import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { apiPath, API_BASE_URL } from '../config/apiConfig';
import {
  LoginRequest,
  LoginResponse,
  User,
  Application,
  ApplicationOwner,
  ApiResponse,
  CreateApplicationRequest,
  UpdateApplicationRequest,
  IGAEntitlement,
  EntitlementMembership,
  CreateEntitlementRequest,
  UpdateEntitlementRequest
} from '../types';

import type { UserRole } from '../types';
import { logApiActivityAudit } from '../utils/apiAuditLogger';
import { isHttpOnlyAuthSessionEnabled, getAuthToken } from '../utils/authSession';

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  withCredentials: isHttpOnlyAuthSessionEnabled(), // Reduced timeout to 15 seconds for better UX
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache'
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    if (!isHttpOnlyAuthSessionEnabled()) {
      const token = getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: auth errors + activity audit for mutating calls
api.interceptors.response.use(
  (response: AxiosResponse) => {
    const method = response.config.method || 'GET';
    const url = response.config.url || '';
    const fullUrl = url.startsWith('http') ? url : `${response.config.baseURL || ''}${url}`;
    void logApiActivityAudit(
      method,
      fullUrl,
      response.status,
      response.config.data,
      response.data,
      response.headers as Record<string, unknown>
    );
    return response;
  },
  (error) => {
    const requestUrl = String(error.config?.url || '');
    const isLoginAttempt = requestUrl.includes('/auth/login');
    // A bad password is a 401 on the login call. Clearing storage here also
    // signs out a session that is already open in another tab.
    if (error.response?.status === 401 && !isLoginAttempt) {
      if (!isHttpOnlyAuthSessionEnabled()) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  // Login
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post<ApiResponse<LoginResponse>>(apiPath('/auth/login'), credentials);
    return response.data.data!;
  },

  // Get profile
  getProfile: async (): Promise<User> => {
    const response = await api.get<ApiResponse<User>>(apiPath('/auth/profile'));
    return response.data.data!;
  },

  // Validate token
  validateToken: async (): Promise<boolean> => {
    try {
      await api.get(apiPath('/auth/validate'));
      return true;
    } catch {
      return false;
    }
  },

  logout: async (): Promise<void> => {
    await api.post(apiPath('/auth/logout'));
  },

  changePassword: async (payload: { current_password: string; new_password: string }): Promise<void> => {
    await api.post<ApiResponse<void>>(apiPath('/auth/profile/change-password'), payload);
  },

  /** Tenant-scoped login page SSO (from Origin); no cross-tenant Okta URLs. */
  getPublicTenantBranding: async (): Promise<import('../utils/tenantBranding').PublicTenantBrandingDTO> => {
    const response = await api.get<ApiResponse<import('../utils/tenantBranding').PublicTenantBrandingDTO>>(
      apiPath('/auth/public/tenant-branding')
    );
    return response.data.data!;
  },

  /** Public branding + login metadata (hostname-resolved on server). */
  getPublicTenantConfig: async (): Promise<import('../utils/tenantConfig').PublicTenantConfigDTO> => {
    const { detectTenantSlugFromHost } = await import('../utils/tenantConfig');
    const slug = detectTenantSlugFromHost();
    const qs = slug ? `?slug=${encodeURIComponent(slug)}` : '';
    const response = await api.get<ApiResponse<import('../utils/tenantConfig').PublicTenantConfigDTO>>(
      apiPath(`/public/tenant-config${qs}`)
    );
    return response.data.data!;
  },

  getPublicLoginOptions: async (): Promise<PublicLoginOptionsDTO> => {
    const response = await api.get<ApiResponse<PublicLoginOptionsDTO>>(
      apiPath('/auth/public/login-options')
    );
    return (
      response.data.data ?? {
        tenant_id: null,
        enterprise_id: null,
        sso_configured: false,
        sso_login_url: null,
        saml_sp_configured: false,
        sso_disabled_hint:
          'SSO is not configured for this tenant. An administrator can configure sign-in under Settings → SSO.'
      }
    );
  },
};

export type PublicLoginOptionsDTO = {
  tenant_id: string | null;
  enterprise_id: string | null;
  sso_configured: boolean;
  sso_login_url: string | null;
  saml_sp_configured: boolean;
  sso_disabled_hint: string;
};

// Dashboard API
export const dashboardAPI = {
  // Get dashboard statistics
  getStats: async (): Promise<any> => {
    const response = await api.get<ApiResponse<any>>(apiPath('/dashboard/stats'));
    return response.data.data!;
  },
};

export interface ReportPayloadDto {
  id: string;
  title: string;
  description: string;
  color: string;
  data: Record<string, unknown>[];
  columns: { key: string; label: string }[];
  lastGenerated: string;
}

export const reportsAPI = {
  getReports: async (): Promise<{ reports: ReportPayloadDto[]; warnings: string[] }> => {
    const response = await api.get<ApiResponse<{ reports: ReportPayloadDto[]; warnings: string[] }>>(
      apiPath('/reports')
    );
    return response.data.data ?? { reports: [], warnings: [] };
  },
};

// Entra ID API
export const entraAPI = {
  // Get application templates
  getTemplates: async (filter?: string): Promise<any[]> => {
    const params = filter ? `?filter=${encodeURIComponent(filter)}` : '';
    const response = await api.get<ApiResponse<any[]>>(apiPath(`/entra/templates${params}`));
    return response.data.data!;
  },

  // Create non-gallery application using template
  createNonGalleryApplication: async (configData: {
    displayName: string;
    templateName: string;
    entityId?: string;
    acsUrl?: string;
    redirectUris?: string[];
    identifierUris?: string[];
  }): Promise<any> => {
    const response = await api.post<ApiResponse<any>>(apiPath('/entra/applications/non-gallery'), configData);
    return response.data.data!;
  },

  // Create SAML application using template
  createSAMLApplication: async (configData: {
    displayName: string;
    entityId: string;
    acsUrl: string;
    nameIdFormat?: string;
    x509Certificate?: string;
    templateName?: string;
  }): Promise<any> => {
    const response = await api.post<ApiResponse<any>>(apiPath('/entra/applications/saml'), configData);
    return response.data.data!;
  },

  // Get EntraID configuration
  getConfig: async (): Promise<any> => {
    const response = await api.get<ApiResponse<any>>(apiPath('/entra/config'));
    return response.data.data || null;
  },

  /** Directory users for bulk import (Microsoft Graph). */
  listUsers: (params?: { q?: string; limit?: number; after?: string }): Promise<
    AxiosResponse<
      ApiResponse<{
        users: Array<{
          id: string;
          status: string;
          profile: { login: string; email: string; firstName: string; lastName: string; displayName: string };
        }>;
        next_after: string | null;
      }>
    >
  > => api.get(apiPath('/entra/users'), { params }),

  // Save EntraID configuration
  saveConfig: async (config: {
    tenant_id: string;
    client_id: string;
    client_secret: string;
  }): Promise<any> => {
    const response = await api.post<ApiResponse<any>>(apiPath('/entra/config'), config);
    return response.data.data || null;
  },

  // Test EntraID connection
  testConnection: async (credentials?: {
    tenantId: string;
    clientId: string;
    clientSecret: string;
  }): Promise<{ connected: boolean }> => {
    const response = await api.post<ApiResponse<{ connected: boolean }>>(apiPath('/entra/test-connection'), credentials || {});
    return response.data.data || { connected: false };
  },

  // Get onboarding automation settings
  getOnboardingSettings: async (): Promise<any[]> => {
    const response = await api.get<ApiResponse<any[]>>(apiPath('/entra/onboarding-settings'));
    return response.data.data || [];
  },

  // Update onboarding automation settings
  updateOnboardingSettings: async (settings: { [key: string]: boolean }): Promise<void> => {
    await api.put<ApiResponse<void>>(apiPath('/entra/onboarding-settings'), { settings });
  },

  // Create application in EntraID
  createApplication: async (appData: {
    displayName: string;
    signInAudience: string;
    web?: {
      redirectUris: string[];
    };
    spa?: {
      redirectUris: string[];
    };
    samlMetadataUrl?: string;
  }): Promise<any> => {
    const response = await api.post<ApiResponse<any>>(apiPath('/entra/applications'), appData);
    return response.data.data || null;
  },

  // Get application from EntraID
  getApplication: async (appId: string): Promise<any> => {
    const response = await api.get<ApiResponse<any>>(apiPath(`/entra/applications/${appId}`));
    return response.data.data || null;
  },

  // Update application in EntraID
  updateApplication: async (appId: string, appData: any): Promise<any> => {
    const response = await api.put<ApiResponse<any>>(apiPath(`/entra/applications/${appId}`), appData);
    return response.data.data || null;
  },

  // Delete application from EntraID
  deleteApplication: async (appId: string): Promise<void> => {
    await api.delete<ApiResponse<void>>(apiPath(`/entra/applications/${appId}`));
  },

  // Get onboarding logs
  getOnboardingLogs: async (ssoConfigurationId?: string): Promise<any[]> => {
    const params = ssoConfigurationId ? { ssoConfigurationId } : {};
    const response = await api.get<ApiResponse<any[]>>(apiPath('/entra/onboarding-logs'), { params });
    return response.data.data || [];
  },
};


// Users API
export type CreateUserPayload = Partial<User> & { password?: string };

export const usersAPI = {
  // Get all users
  getUsers: async (): Promise<User[]> => {
    // User Management page only — includes inactive users; requires USER_LIST_READ_ROLES.
    const response = await api.get<ApiResponse<User[]>>(apiPath('/auth/users'));
    return response.data.data!;
  },

  /** Active tenant users for pickers (delegation, owners, approvers, team contacts). Any authenticated tenant user. */
  getUsersForPicker: async (): Promise<User[]> => {
    const response = await api.get<ApiResponse<User[]>>(apiPath('/team-contacts/users'));
    return response.data.data || [];
  },

  // Get user by ID
  getUser: async (id: string): Promise<User> => {
    const response = await api.get<ApiResponse<User>>(apiPath(`/auth/users/${id}`));
    return response.data.data!;
  },

  // Create user
  createUser: async (userData: CreateUserPayload): Promise<User> => {
    const response = await api.post<ApiResponse<User>>(apiPath('/auth/users'), userData);
    return response.data.data!;
  },

  // Update user
  updateUser: async (id: string, userData: Partial<User>): Promise<User> => {
    const response = await api.put<ApiResponse<User>>(apiPath(`/auth/users/${id}`), userData);
    return response.data.data!;
  },

  // Delete user
  deleteUser: async (id: string): Promise<boolean> => {
    await api.delete(apiPath(`/auth/users/${id}`));
    return true;
  },

  suspendUser: async (id: string): Promise<void> => {
    await api.post(apiPath(`/auth/users/${id}/suspend`), { confirm: true });
  },

  // Download Excel template
  downloadTemplate: async (): Promise<Blob> => {
    const response = await api.get(apiPath('/auth/users/template'), {
      responseType: 'blob',
    });
    return response.data;
  },

  // Import users from Excel
  importUsers: async (formData: FormData): Promise<any> => {
    const response = await api.post<ApiResponse<any>>(apiPath('/auth/users/import'), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data!;
  },

  importUsersFromIdp: async (users: Array<{ username: string; email: string; first_name: string; last_name: string; role?: UserRole }>): Promise<{
    imported: number;
    skipped: number;
    errors: string[];
  }> => {
    const response = await api.post<ApiResponse<{ imported: number; skipped: number; errors: string[] }>>(
      apiPath('/auth/users/import-from-idp'),
      { users }
    );
    return response.data.data!;
  },

  resetUserPassword: async (userId: string, new_password: string): Promise<void> => {
    await api.post<ApiResponse<void>>(apiPath(`/auth/users/${userId}/reset-password`), { new_password });
  },
};

// Applications API
export const applicationsAPI = {
  // Get all applications
  getApplications: async (): Promise<Application[]> => {
    const response = await api.get<ApiResponse<Application[]>>(apiPath('/applications'));
    return response.data.data!;
  },

  // Get application by ID
  getApplication: async (id: string): Promise<Application> => {
    const response = await api.get<ApiResponse<Application>>(apiPath(`/applications/${id}`));
    return response.data.data!;
  },

  // Get application details with owners and entitlements
  getApplicationDetails: async (id: string): Promise<any> => {
    const response = await api.get<ApiResponse<any>>(apiPath(`/applications/${id}/details`));
    return response.data.data!;
  },

  // Create application
  createApplication: async (appData: CreateApplicationRequest): Promise<Application> => {
    const response = await api.post<ApiResponse<Application>>(apiPath('/applications'), appData);
    return response.data.data!;
  },

  // Update application
  updateApplication: async (id: string, appData: UpdateApplicationRequest): Promise<Application> => {
    const response = await api.put<ApiResponse<Application>>(apiPath(`/applications/${id}`), appData);
    return response.data.data!;
  },

  // Delete application
  deleteApplication: async (id: string): Promise<boolean> => {
    await api.delete(apiPath(`/applications/${id}`));
    return true;
  },

  // Search applications
  searchApplications: async (searchTerm: string): Promise<Application[]> => {
    const response = await api.get<ApiResponse<Application[]>>(apiPath(`/applications?search=${searchTerm}`));
    return response.data.data!;
  },

  // Get applications by platform
  getApplicationsByPlatform: async (platform: string): Promise<Application[]> => {
    const response = await api.get<ApiResponse<Application[]>>(apiPath(`/applications?platform=${platform}`));
    return response.data.data!;
  },

  // Get applications by status
  getApplicationsByStatus: async (status: string): Promise<Application[]> => {
    const response = await api.get<ApiResponse<Application[]>>(apiPath(`/applications?status=${status}`));
    return response.data.data!;
  },

  // Get application owners
  getApplicationOwners: async (id: string): Promise<ApplicationOwner[]> => {
    const response = await api.get<ApiResponse<ApplicationOwner[]>>(apiPath(`/applications/${id}/owners`));
    return response.data.data!;
  },

  // Add application owner
  addApplicationOwner: async (id: string, userId: string, ownershipType: string): Promise<ApplicationOwner> => {
    const response = await api.post<ApiResponse<ApplicationOwner>>(apiPath(`/applications/${id}/owners`), {
      user_id: userId,
      ownership_type: ownershipType,
    });
    return response.data.data!;
  },

  // Remove application owner
  removeApplicationOwner: async (id: string, userId: string): Promise<boolean> => {
    await api.delete(apiPath(`/applications/${id}/owners/${userId}`));
    return true;
  },
};

// Notifications API
export const notificationAPI = {
  // Get notifications for current user
  getNotifications: async (limit: number = 50): Promise<any[]> => {
    const response = await api.get<ApiResponse<any[]>>(apiPath(`/notifications?limit=${limit}`));
    return response.data.data || [];
  },

  // Get unread notification count
  getUnreadCount: async (): Promise<number> => {
    const response = await api.get<ApiResponse<{count: number}>>(apiPath('/notifications/unread-count'));
    return response.data.data?.count || 0;
  },

  // Mark notification as read
  markAsRead: async (notificationId: string): Promise<boolean> => {
    await api.put(apiPath(`/notifications/${notificationId}/read`));
    return true;
  },

  // Mark all notifications as read
  markAllAsRead: async (): Promise<boolean> => {
    await api.put(apiPath('/notifications/mark-all-read'));
    return true;
  },
};

// Team Contacts API
export const teamContactsAPI = {
  // Get all team contacts
  getAllContacts: async (): Promise<any[]> => {
    const response = await api.get<ApiResponse<any[]>>(apiPath('/team-contacts'));
    return response.data.data || [];
  },

  // Get contacts by team type
  getContactsByTeamType: async (teamType: 'SSO' | 'PAM' | 'IGA'): Promise<any[]> => {
    const response = await api.get<ApiResponse<any[]>>(apiPath(`/team-contacts/${teamType}`));
    return response.data.data || [];
  },

  // Create new team contact
  createContact: async (contactData: {
    team_type: 'SSO' | 'PAM' | 'IGA';
    user_id: string;
    role: string;
  }): Promise<any> => {
    const response = await api.post<ApiResponse<any>>(apiPath('/team-contacts'), contactData);
    return response.data.data;
  },

  // Update team contact
  updateContact: async (id: number, contactData: {
    team_type?: 'SSO' | 'PAM' | 'IGA';
    user_id?: string;
    role?: string;
  }): Promise<any> => {
    const response = await api.put<ApiResponse<any>>(apiPath(`/team-contacts/${id}`), contactData);
    return response.data.data;
  },

  // Delete team contact
  deleteContact: async (id: number): Promise<boolean> => {
    await api.delete(apiPath(`/team-contacts/${id}`));
    return true;
  },

  // Get team info
  getTeamInfo: async (teamType: 'SSO' | 'PAM' | 'IGA'): Promise<any> => {
    const response = await api.get<ApiResponse<any>>(apiPath(`/team-contacts/team-info/${teamType}`));
    return response.data.data;
  },

  // Update team info
  updateTeamInfo: async (teamType: 'SSO' | 'PAM' | 'IGA', teamInfo: {
    servicenow_queue_name?: string;
    email_dg?: string;
  }): Promise<any> => {
    const response = await api.put<ApiResponse<any>>(apiPath(`/team-contacts/team-info/${teamType}`), teamInfo);
    return response.data.data;
  },

  // Get all users for dropdown
  getUsers: async (): Promise<any[]> => {
    const response = await api.get<ApiResponse<any[]>>(apiPath('/team-contacts/users'));
    return response.data.data || [];
  },
};

// Health check
export const healthAPI = {
  check: async (): Promise<boolean> => {
    try {
      await api.get('/health');
      return true;
    } catch {
      return false;
    }
  },
};


// Provider API
export const providerAPI = {
  // Get all provider types
  getProviderTypes: async (): Promise<any[]> => {
    const response = await api.get<ApiResponse<any[]>>(apiPath('/providers/types'));
    return response.data.data || [];
  },

  // Get provider types by category
  getProviderTypesByCategory: async (category: 'idp' | 'iga' | 'pam'): Promise<any[]> => {
    const response = await api.get<ApiResponse<any[]>>(apiPath(`/providers/types/${category}`));
    return response.data.data || [];
  },

  // Get all provider configurations
  getProviderConfigs: async (): Promise<any[]> => {
    const response = await api.get<ApiResponse<any[]>>(apiPath('/providers/configs'));
    return response.data.data || [];
  },

  // Get provider configurations by category
  getProviderConfigsByCategory: async (category: 'idp' | 'iga' | 'pam'): Promise<any[]> => {
    const response = await api.get<ApiResponse<any[]>>(apiPath(`/providers/configs/${category}`));
    return response.data.data || [];
  },

  // Get specific provider configuration
  getProviderConfig: async (id: number): Promise<any> => {
    const response = await api.get<ApiResponse<any>>(apiPath(`/providers/configs/detail/${id}`));
    return response.data.data || null;
  },

  // Create provider configuration
  createProviderConfig: async (config: {
    name: string;
    provider_type_id: number;
    configuration: any;
    is_active?: boolean;
  }): Promise<any> => {
    const response = await api.post<ApiResponse<any>>(apiPath('/providers/configs'), config);
    return response.data.data || null;
  },

  // Update provider configuration
  updateProviderConfig: async (id: number, config: {
    name?: string;
    configuration?: any;
    is_active?: boolean;
  }): Promise<any> => {
    const response = await api.put<ApiResponse<any>>(apiPath(`/providers/configs/${id}`), config);
    return response.data.data || null;
  },

  // Delete provider configuration
  deleteProviderConfig: async (id: number): Promise<void> => {
    await api.delete<ApiResponse<void>>(apiPath(`/providers/configs/${id}`));
  },

  // Test provider connection
  testProviderConnection: async (id: number): Promise<{ connected: boolean }> => {
    const response = await api.post<ApiResponse<{ connected: boolean }>>(apiPath(`/providers/configs/${id}/test`));
    return response.data.data || { connected: false };
  },

  // Get automation settings
  getAutomationSettings: async (): Promise<any[]> => {
    const response = await api.get<ApiResponse<any[]>>(apiPath('/providers/automation-settings'));
    return response.data.data || [];
  },

  // Update automation settings
  updateAutomationSettings: async (settings: { [key: string]: boolean }): Promise<void> => {
    await api.put<ApiResponse<void>>(apiPath('/providers/automation-settings'), { settings });
  },

  // Get automation logs
  getAutomationLogs: async (ssoConfigurationId?: string): Promise<any[]> => {
    const params = ssoConfigurationId ? { ssoConfigurationId } : {};
    const response = await api.get<ApiResponse<any[]>>(apiPath('/providers/automation-logs'), { params });
    return response.data.data || [];
  },
};

// User Profile API
export const userProfileAPI = {
  getUserProfile: (userId: string) =>
    api.get(apiPath(`/user-profile/${userId}`)),
  getAllUsers: () =>
    api.get(apiPath('/user-profile')),
};

// Theme API
export const themeAPI = {
  getThemeSettings: () =>
    api.get(apiPath('/theme')),
  saveThemeSettings: (themeData: { theme_name: string; custom_colors?: any }) =>
    api.post(apiPath('/theme'), themeData),
  resetThemeSettings: () =>
    api.post(apiPath('/theme/reset')),
};

export const brandingAPI = {
  saveBranding: (payload: {
    company_name?: string;
    company_tagline?: string;
    logo?: string;
    logo_mark?: string;
    logo_on_dark?: string;
  }): Promise<AxiosResponse<ApiResponse<void>>> => api.post(apiPath('/theme/branding'), payload),
};

// SSO API
export const ssoAPI = {
  // Get SSO configurations
  getConfigurations: (): Promise<AxiosResponse<ApiResponse<any[]>>> =>
    api.get(apiPath('/sso/configurations')),
  
  // Get SSO configurations by enterprise (for SSO admins)
  getConfigurationsByEnterprise: (): Promise<AxiosResponse<ApiResponse<any[]>>> =>
    api.get(apiPath('/sso/configurations/enterprise')),
  
  // Get SSO configurations by user (for application owners)
  getConfigurationsByUser: (): Promise<AxiosResponse<ApiResponse<any[]>>> =>
    api.get(apiPath('/sso/configurations/user')),
  
  // Get SSO configuration by ID
  getConfiguration: (configId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(apiPath(`/sso/configurations/${configId}`)),
  
  // Create SSO configuration
  createConfiguration: (configData: any): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(apiPath('/sso/configurations'), configData),
  
  // Update SSO configuration
  updateConfiguration: (configId: string, updateData: any): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.put(apiPath(`/sso/configurations/${configId}`), updateData),
  
  // Delete SSO configuration
  deleteConfiguration: (configId: string): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.delete(apiPath(`/sso/configurations/${configId}`)),
  
  // Renew client secret
  renewClientSecret: (configId: string): Promise<AxiosResponse<ApiResponse<{ client_secret: string; client_secret_expires_at: string }>>> =>
    api.post(apiPath(`/sso/configurations/${configId}/renew-secret`)),

  checkoutClientSecret: (configId: string): Promise<AxiosResponse<ApiResponse<{ client_secret: string }>>> =>
    api.post(apiPath(`/sso/configurations/${configId}/checkout-secret`)),
  
  // Get SSO statistics
  getStatistics: (): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(apiPath('/sso/statistics')),
  
  // Get SSO notes
  getNotes: (configId: string): Promise<AxiosResponse<ApiResponse<any[]>>> =>
    api.get(apiPath(`/sso/configurations/${configId}/notes`)),

  /** Bulk notes summary for unread badges (one call). POST body: { last_viewed?: { [configId]: iso } } */
  getNotesSummary: (lastViewed?: Record<string, string>): Promise<AxiosResponse<ApiResponse<Array<{
    configuration_id: string;
    notes_count: number;
    unread_count: number;
    latest_created_at: string | null;
  }>>>> =>
    api.post(apiPath('/sso/configurations/notes-summary'), { last_viewed: lastViewed || {} }),
  
  // Add SSO note
  addNote: (configId: string, noteData: any): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(apiPath(`/sso/configurations/${configId}/notes`), noteData),
  
  // Update SSO configuration status
  updateConfigurationStatus: (configId: string, status: string, notes: string, assignedTo?: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.put(apiPath(`/sso/configurations/${configId}/status`), {
      status,
      review_notes: notes,
      assigned_to: assignedTo?.trim() ? assignedTo : null,
    }),

  assignConfiguration: (configId: string, assignedTo: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.put(apiPath(`/sso/configurations/${configId}/assignee`), { assigned_to: assignedTo }),
  
  // Get SSO admins for assignment
  getAdmins: (): Promise<AxiosResponse<ApiResponse<any[]>>> =>
    api.get(apiPath('/sso/admins')),
  
  // Upload metadata XML file
  uploadMetadata: (file: File): Promise<AxiosResponse<ApiResponse<any>>> => {
    const formData = new FormData();
    formData.append('metadata', file);
    return api.post(apiPath('/sso/upload-metadata'), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // Fetch metadata from URL
  fetchMetadataFromURL: (metadataUrl: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(apiPath('/sso/fetch-metadata-url'), { metadata_url: metadataUrl }),
  
  // Get SSO configurations by application
  getConfigurationsByApplication: (appId: string): Promise<AxiosResponse<ApiResponse<any[]>>> =>
    api.get(apiPath(`/sso/configurations/application/${appId}`)),
  
  // Get SSO configuration by ID (alias for getConfiguration)
  getConfigurationById: (configId: string): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.get(apiPath(`/sso/configurations/${configId}`)),
  
  // Fetch Azure applications with owners (Graph API can be slow for large tenants)
  fetchAzureApplications: (): Promise<AxiosResponse<ApiResponse<any[]>>> =>
    api.get(apiPath('/sso/azure-applications'), { timeout: 120000 }),

  fetchOktaApplications: (): Promise<AxiosResponse<ApiResponse<any[]>>> =>
    api.get(apiPath('/sso/okta-applications'), { timeout: 120000 }),
  
  // Import Azure applications with owner assignment
  importAzureApplications: (applications: any[]): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(apiPath('/sso/import-azure-applications'), { applications }, { timeout: 120000 }),

  importOktaApplications: (applications: any[]): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(apiPath('/sso/import-okta-applications'), { applications }, { timeout: 120000 }),

  getConfiguredImportIdps: (): Promise<
    AxiosResponse<ApiResponse<{ options: Array<{ id: string; label: string }> }>>
  > => api.get(apiPath('/sso/configured-import-idps')),
};

// IGA API
export const igaAPI = {
  getEntitlements: (): Promise<AxiosResponse<ApiResponse<IGAEntitlement[]>>> =>
    api.get(apiPath('/iga/entitlements')),
  
  getEntitlementsByInstance: (instanceId: string): Promise<AxiosResponse<ApiResponse<IGAEntitlement[]>>> =>
    api.get(apiPath(`/iga/entitlements/instance/${instanceId}`)),

  /** IGA entitlements assigned to the user (membership rows). Same auth as user profile: self or super_admin. */
  getEntitlementsForUser: (userId: string): Promise<AxiosResponse<ApiResponse<IGAEntitlement[]>>> =>
    api.get(apiPath(`/iga/entitlements/user/${userId}`)),

  createEntitlement: (entitlementData: CreateEntitlementRequest): Promise<AxiosResponse<ApiResponse<IGAEntitlement>>> =>
    api.post(apiPath('/iga/entitlements'), entitlementData),
  
  updateEntitlement: (entitlementId: string, updateData: UpdateEntitlementRequest): Promise<AxiosResponse<ApiResponse<IGAEntitlement>>> =>
    api.put(apiPath(`/iga/entitlements/${entitlementId}`), updateData),
  
  deleteEntitlement: (entitlementId: string): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.delete(apiPath(`/iga/entitlements/${entitlementId}`)),
  
  getEntitlementMemberships: (entitlementId: string): Promise<AxiosResponse<ApiResponse<EntitlementMembership[]>>> =>
    api.get(apiPath(`/iga/entitlements/${entitlementId}/memberships`)),

  downloadEntitlementTemplate: async (): Promise<Blob> => {
    const response = await api.get(apiPath('/iga/entitlements/template'), {
      responseType: 'blob',
    });
    return response.data;
  },

  importEntitlements: async (formData: FormData): Promise<{
    success: boolean;
    totalRows: number;
    successfulImports: number;
    failedImports: number;
    errors: string[];
  }> => {
    const response = await api.post<ApiResponse<{
      success: boolean;
      totalRows: number;
      successfulImports: number;
      failedImports: number;
      errors: string[];
    }>>(apiPath('/iga/entitlements/import'), formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data!;
  },
};

// AI Governance API
export const aiGovernanceAPI = {
  listManageableApplications: (): Promise<
    AxiosResponse<ApiResponse<Array<{ id: string; name: string }>>>
  > => api.get(apiPath('/ai-governance/applications/manageable')),

  getEnums: (): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIGovernanceEnums>>> =>
    api.get(apiPath('/ai-governance/meta/enums')),

  listAssets: (params?: {
    application_id?: string;
    asset_type?: string;
    status?: string;
    search?: string;
  }): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAsset[]>>> =>
    api.get(apiPath('/ai-governance/assets'), { params }),

  getAsset: (id: string): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAsset>>> =>
    api.get(apiPath(`/ai-governance/assets/${id}`)),

  createAsset: (
    data: import('../types/aiGovernance').CreateAIAssetRequest
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAsset>>> =>
    api.post(apiPath('/ai-governance/assets'), data),

  updateAsset: (
    id: string,
    data: Partial<import('../types/aiGovernance').CreateAIAssetRequest>
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAsset>>> =>
    api.put(apiPath(`/ai-governance/assets/${id}`), data),

  getActivationReadiness: (
    id: string
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAssetActivationReadiness>>> =>
    api.get(apiPath(`/ai-governance/assets/${id}/activation-readiness`)),

  getRiskAssessment: (
    id: string
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAssetRiskAssessment | null>>> =>
    api.get(apiPath(`/ai-governance/assets/${id}/risk-assessment`)),

  ensureRiskAssessment: (
    id: string
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAssetRiskAssessment>>> =>
    api.post(apiPath(`/ai-governance/assets/${id}/risk-assessment`)),

  saveRiskAnswers: (
    id: string,
    answers: Record<string, unknown>
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAssetRiskAssessment>>> =>
    api.put(apiPath(`/ai-governance/assets/${id}/risk-assessment/answers`), { answers }),

  submitRiskAssessment: (
    id: string
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAssetRiskAssessment>>> =>
    api.post(apiPath(`/ai-governance/assets/${id}/risk-assessment/submit`)),

  activateAsset: (
    id: string
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAsset>>> =>
    api.post(apiPath(`/ai-governance/assets/${id}/activate`)),

  retireAsset: (
    id: string
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAsset> & { revoked_grants?: number }>> =>
    api.post(apiPath(`/ai-governance/assets/${id}/retire`)),

  getDataScopeOptions: (
    appId: string
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIDataScopeOptions>>> =>
    api.get(apiPath(`/ai-governance/applications/${appId}/data-scope-options`)),

  submitAccessRequest: (
    data: import('../types/aiGovernance').CreateAIAccessRequestInput
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAccessGrant>>> =>
    api.post(apiPath('/ai-governance/access-requests'), data),

  listAccessRequests: (): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAccessGrant[]>>> =>
    api.get(apiPath('/ai-governance/access-requests')),

  listCertificationCampaigns: (): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAccessGrant[]>>> =>
    api.get(apiPath('/ai-governance/access-requests/campaigns')),

  listInboundRequests: (
    appId: string,
    status?: string
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAccessGrant[]>>> =>
    api.get(apiPath(`/ai-governance/applications/${appId}/inbound-requests`), { params: status ? { status } : undefined }),

  listApplicationGrants: (
    appId: string,
    status?: string
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAccessGrant[]>>> =>
    api.get(apiPath(`/ai-governance/applications/${appId}/grants`), { params: status ? { status } : undefined }),

  getAccessGrant: (
    grantId: string
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAccessGrant>>> =>
    api.get(apiPath(`/ai-governance/access-grants/${grantId}`)),

  decideAccessGrant: (
    grantId: string,
    data: import('../types/aiGovernance').DecideAIAccessGrantInput
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAccessGrant>>> =>
    api.post(apiPath(`/ai-governance/access-grants/${grantId}/decide`), data),

  revokeAccessGrant: (
    grantId: string,
    reason: string
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAccessGrant>>> =>
    api.post(apiPath(`/ai-governance/access-grants/${grantId}/revoke`), { reason }),

  recertifyAccessGrant: (
    grantId: string
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIAccessGrant>>> =>
    api.post(apiPath(`/ai-governance/access-grants/${grantId}/recertify`)),

  listCapabilities: (
    assetId: string
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AICapability[]>>> =>
    api.get(apiPath(`/ai-governance/assets/${assetId}/capabilities`)),

  createCapability: (
    assetId: string,
    data: import('../types/aiGovernance').CreateAICapabilityInput
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AICapability>>> =>
    api.post(apiPath(`/ai-governance/assets/${assetId}/capabilities`), data),

  updateCapability: (
    assetId: string,
    capId: string,
    data: Partial<import('../types/aiGovernance').CreateAICapabilityInput>
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AICapability>>> =>
    api.put(apiPath(`/ai-governance/assets/${assetId}/capabilities/${capId}`), data),

  deleteCapability: (assetId: string, capId: string): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.delete(apiPath(`/ai-governance/assets/${assetId}/capabilities/${capId}`)),

  listTools: (
    assetId: string
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AITool[]>>> =>
    api.get(apiPath(`/ai-governance/assets/${assetId}/tools`)),

  createTool: (
    assetId: string,
    data: import('../types/aiGovernance').CreateAIToolInput
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AITool>>> =>
    api.post(apiPath(`/ai-governance/assets/${assetId}/tools`), data),

  updateTool: (
    assetId: string,
    toolId: string,
    data: Partial<import('../types/aiGovernance').CreateAIToolInput>
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AITool>>> =>
    api.put(apiPath(`/ai-governance/assets/${assetId}/tools/${toolId}`), data),

  deleteTool: (assetId: string, toolId: string): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.delete(apiPath(`/ai-governance/assets/${assetId}/tools/${toolId}`)),

  listDataSources: (
    assetId: string
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIDataSource[]>>> =>
    api.get(apiPath(`/ai-governance/assets/${assetId}/data-sources`)),

  createDataSource: (
    assetId: string,
    data: import('../types/aiGovernance').CreateAIDataSourceInput
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIDataSource>>> =>
    api.post(apiPath(`/ai-governance/assets/${assetId}/data-sources`), data),

  updateDataSource: (
    assetId: string,
    dsId: string,
    data: Partial<import('../types/aiGovernance').CreateAIDataSourceInput>
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIDataSource>>> =>
    api.put(apiPath(`/ai-governance/assets/${assetId}/data-sources/${dsId}`), data),

  deleteDataSource: (assetId: string, dsId: string): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.delete(apiPath(`/ai-governance/assets/${assetId}/data-sources/${dsId}`)),

  listPrompts: (
    assetId: string
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIPrompt[]>>> =>
    api.get(apiPath(`/ai-governance/assets/${assetId}/prompts`)),

  createPrompt: (
    assetId: string,
    data: import('../types/aiGovernance').CreateAIPromptInput
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIPrompt>>> =>
    api.post(apiPath(`/ai-governance/assets/${assetId}/prompts`), data),

  updatePrompt: (
    assetId: string,
    promptId: string,
    data: Partial<import('../types/aiGovernance').CreateAIPromptInput>
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIPrompt>>> =>
    api.put(apiPath(`/ai-governance/assets/${assetId}/prompts/${promptId}`), data),

  createPromptVersion: (
    assetId: string,
    promptId: string,
    data: import('../types/aiGovernance').CreateAIPromptVersionInput
  ): Promise<
    AxiosResponse<
      ApiResponse<{ version: { version_number: number }; prompt: import('../types/aiGovernance').AIPrompt }>
    >
  > => api.post(apiPath(`/ai-governance/assets/${assetId}/prompts/${promptId}/versions`), data),

  deletePrompt: (assetId: string, promptId: string): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.delete(apiPath(`/ai-governance/assets/${assetId}/prompts/${promptId}`)),

  getSettings: (): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIGovernanceSettings>>> =>
    api.get(apiPath('/ai-governance/settings')),

  updateSettings: (
    data: import('../types/aiGovernance').UpdateAIGovernanceSettingsInput
  ): Promise<AxiosResponse<ApiResponse<import('../types/aiGovernance').AIGovernanceSettings>>> =>
    api.put(apiPath('/ai-governance/settings'), data),

  getSchedulerStatus: (): Promise<
    AxiosResponse<ApiResponse<import('../types/aiGovernance').AIGovernanceSchedulerStatus>>
  > => api.get(apiPath('/ai-governance/scheduler/status')),

  runScheduler: (): Promise<
    AxiosResponse<
      ApiResponse<{
        runId: string;
        expiredCount: number;
        expiryWarningCount: number;
        recertFlaggedCount: number;
        recertReminderCount: number;
        errorCount: number;
        skipped?: boolean;
        reason?: string;
      }>
    >
  > => api.post(apiPath('/ai-governance/scheduler/run')),

  getDashboardMetrics: (): Promise<
    AxiosResponse<ApiResponse<import('../types/aiGovernance').AIGovernanceDashboardMetrics>>
  > => api.get(apiPath('/ai-governance/dashboard/metrics')),

  exportCsv: (type: 'assets' | 'grants'): Promise<AxiosResponse<Blob>> =>
    api.get(apiPath('/ai-governance/export'), { params: { type }, responseType: 'blob' }),
};

// Okta integration API
export const oktaAPI = {
  getConfig: (): Promise<AxiosResponse<ApiResponse<{
    org_url?: string;
    api_token?: string;
    is_configured?: boolean;
    updated_at?: string;
  }>>> => api.get(apiPath('/okta/config')),

  testConnection: (body?: Record<string, unknown>): Promise<AxiosResponse<ApiResponse<any>>> =>
    api.post(apiPath('/okta/test-connection'), body || {}),

  listUsers: (params?: { q?: string; limit?: number; after?: string }): Promise<
    AxiosResponse<
      ApiResponse<{
        users: Array<{
          id: string;
          status: string;
          profile: { login: string; email: string; firstName: string; lastName: string; displayName: string };
        }>;
        next_after: string | null;
      }>
    >
  > => api.get(apiPath('/okta/users'), { params }),

  /** Pull SAML attribute statements from Okta (optional existing app id). */
  fetchSamlAttributeStatements: (oktaAppId?: string): Promise<AxiosResponse<ApiResponse<{ attribute_statements: unknown[]; source: string }>>> =>
    api.get(apiPath('/okta/saml-attribute-statements'), {
      params: oktaAppId ? { oktaAppId } : undefined,
    }),

  listSsoAttributes: (
    context: 'management' | 'application' = 'management'
  ): Promise<AxiosResponse<ApiResponse<import('../types').SSOAttribute[]>>> =>
    api.get(apiPath('/okta/sso-attributes'), { params: { context } }),

  syncSsoAttributes: (): Promise<
    AxiosResponse<
      ApiResponse<{
        synced: number;
        attributes: import('../types').SSOAttribute[];
        errors: string[];
        results?: Array<{
          provider: 'okta' | 'azure';
          configured: boolean;
          synced: number;
          errors: string[];
          apps: string[];
        }>;
      }>
    >
  > => api.post(apiPath('/okta/sync-attributes')),

  /** @deprecated use syncSsoAttributes */
  syncSsoAttributesFromOkta: (): Promise<
    AxiosResponse<
      ApiResponse<{
        synced: number;
        attributes: import('../types').SSOAttribute[];
        errors: string[];
      }>
    >
  > => api.post(apiPath('/okta/sync-attributes')),

  createSsoAttribute: (
    payload: Partial<import('../types').SSOAttribute>
  ): Promise<AxiosResponse<ApiResponse<import('../types').SSOAttribute>>> =>
    api.post(apiPath('/okta/sso-attributes'), payload),

  updateSsoAttribute: (
    id: string,
    payload: Partial<import('../types').SSOAttribute>
  ): Promise<AxiosResponse<ApiResponse<import('../types').SSOAttribute>>> =>
    api.put(apiPath(`/okta/sso-attributes/${id}`), payload),

  deleteSsoAttribute: (id: string): Promise<AxiosResponse<ApiResponse<void>>> =>
    api.delete(apiPath(`/okta/sso-attributes/${id}`)),
};

// PAM API
export const pamAPI = {
  getAccounts: () => api.get(apiPath('/pam/accounts')),
  getAccountById: (accountId: string) => api.get(apiPath(`/pam/accounts/${accountId}`)),
  getAccountsByApplication: (applicationId: string) => api.get(apiPath(`/pam/accounts/application/${applicationId}`)),
  getUsageLogs: (accountId?: string, limit?: number) => 
    api.get(apiPath('/pam/usage-logs'), { params: { accountId, limit } }),
  getDependencies: (applicationId?: string) => 
    api.get(apiPath('/pam/dependencies'), { params: { applicationId } }),
  getComplianceReport: () => api.get(apiPath('/pam/compliance-report')),
  getAccountsByClassification: (classification: string) => 
    api.get(apiPath(`/pam/accounts/classification/${classification}`)),
  getHighRiskAccounts: (riskThreshold?: number) => 
    api.get(apiPath('/pam/accounts/high-risk'), { params: { riskThreshold } }),
  getStaleAccounts: (daysThreshold?: number) => 
    api.get(apiPath('/pam/accounts/stale'), { params: { daysThreshold } }),
  getAccountsNearingExpiry: (daysThreshold?: number) => 
    api.get(apiPath('/pam/accounts/nearing-expiry'), { params: { daysThreshold } }),
  getUsageAnalytics: (accountId?: string, days?: number) => 
    api.get(apiPath('/pam/analytics/usage'), { params: { accountId, days } }),
  updateAccountMetadata: (accountId: string, metadata: Record<string, any>, tags: string[]) =>
    api.put(apiPath(`/pam/accounts/${accountId}/metadata`), { metadata, tags }),
  applyAccountAction: (accountId: string, action: 'rotate' | 'disable') =>
    api.post(apiPath(`/pam/accounts/${accountId}/lifecycle`), { action }),
  checkoutAccount: (accountId: string, body: { reason: string; starts_at: string; ends_at: string; approver_id?: string }) =>
    api.post(apiPath(`/pam/accounts/${accountId}/checkout`), body),
};

// Notifications API
export type EnterpriseSamlLoginConfigDTO = {
  enterprise_id: string;
  sp_entity_id: string;
  sp_acs_url: string;
  idp_metadata_url: string | null;
  idp_entity_id: string | null;
  idp_x509_cert: string | null;
  idp_x509_cert_extra: string | null;
  /** Okta (or other IdP) SP-initiated SSO URL for the login page button. */
  idp_sso_login_url: string | null;
  frontend_redirect_url: string | null;
  accepted_clock_skew_ms: number;
  want_assertions_signed: boolean;
  want_response_signed: boolean;
  updated_at: string;
  updated_by: string | null;
  updated_by_name?: string | null;
};

export type EnterpriseSamlLoginConfigSaveInput = {
  sp_entity_id: string;
  sp_acs_url: string;
  idp_metadata_url?: string | null;
  idp_entity_id?: string | null;
  idp_x509_cert?: string | null;
  idp_x509_cert_extra?: string | null;
  idp_sso_login_url?: string | null;
  frontend_redirect_url?: string | null;
  accepted_clock_skew_ms?: number;
  want_assertions_signed?: boolean;
  want_response_signed?: boolean;
};

export type IdpSamlDiscoverResult = {
  tenant_id: string;
  client_id: string;
  sp_entity_id: string;
  sp_acs_url: string;
  idp_metadata_url: string;
  idp_entity_id: string;
  idp_x509_cert: string;
  idp_sso_login_url: string | null;
  want_response_signed: boolean;
};

/** @deprecated use IdpSamlDiscoverResult */
export type AzureSamlDiscoverResult = IdpSamlDiscoverResult;

export const enterpriseSamlLoginAPI = {
  get: async (): Promise<EnterpriseSamlLoginConfigDTO | null> => {
    const response = await api.get<ApiResponse<EnterpriseSamlLoginConfigDTO | null>>(
      apiPath('/enterprise/saml-login-config')
    );
    return response.data.data ?? null;
  },
  save: async (body: EnterpriseSamlLoginConfigSaveInput): Promise<EnterpriseSamlLoginConfigDTO> => {
    const response = await api.put<ApiResponse<EnterpriseSamlLoginConfigDTO>>(
      apiPath('/enterprise/saml-login-config'),
      body
    );
    return response.data.data!;
  },
  discoverFromCredentials: async (body: {
    entity_id: string;
    client_id: string;
    client_secret: string;
    sp_acs_url?: string;
  }): Promise<IdpSamlDiscoverResult> => {
    const response = await api.post<ApiResponse<IdpSamlDiscoverResult>>(
      apiPath('/enterprise/saml-login-config/discover'),
      body
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || response.data.message || 'IdP discovery failed');
    }
    return response.data.data;
  },
};

export type VysorAgentProvider =
  | 'azure_foundry'
  | 'azure_openai'
  | 'openai'
  | 'openai_compatible';

export type VysorRuntimeMode = 'auto' | 'foundry' | 'chat_completions';

export type VysorCapabilityToggle = {
  id: string;
  label: string;
  description?: string;
  enabled: boolean;
};

export type VysorProviderCatalogItem = {
  id: VysorAgentProvider;
  label: string;
  description: string;
};

export type VysorAgentConfigPublic = {
  enterprise_id: string;
  agent_name: string;
  provider?: VysorAgentProvider;
  resource_endpoint: string;
  foundry_project?: string;
  foundry_agent_id?: string;
  runtime_mode?: VysorRuntimeMode;
  deployment_name: string;
  api_version: string;
  instructions: string;
  temperature: number;
  max_tokens: number;
  is_active: boolean;
  api_key_configured: boolean;
  api_key_masked?: string;
  capabilities?: Record<string, boolean>;
  capability_catalog?: VysorCapabilityToggle[];
  provider_catalog?: VysorProviderCatalogItem[];
  updated_at?: string;
};

export const assistantAgentAPI = {
  getConfig: async (): Promise<VysorAgentConfigPublic> => {
    const response = await api.get<ApiResponse<VysorAgentConfigPublic>>(apiPath('/assistant/agent-config'));
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || response.data.message || 'Failed to load agent configuration');
    }
    return response.data.data;
  },
  saveConfig: async (body: Record<string, unknown>): Promise<VysorAgentConfigPublic> => {
    const response = await api.put<ApiResponse<VysorAgentConfigPublic>>(apiPath('/assistant/agent-config'), body);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || response.data.message || 'Failed to save agent configuration');
    }
    return response.data.data;
  },
  getDefaultInstructions: async (): Promise<{ instructions: string }> => {
    const response = await api.get<ApiResponse<{ instructions: string }>>(apiPath('/assistant/agent-config/defaults'));
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || response.data.message || 'Failed to load default instructions');
    }
    return response.data.data;
  },
  testConnection: async (): Promise<{ connected: boolean; message: string }> => {
    const response = await api.post<ApiResponse<{ connected: boolean; message: string }>>(
      apiPath('/assistant/agent-config/test')
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || response.data.message || 'Connection test failed');
    }
    return response.data.data;
  },
  provisionFoundryAgent: async (): Promise<{
    agent_id: string;
    created: boolean;
    config: VysorAgentConfigPublic;
  }> => {
    const response = await api.post<
      ApiResponse<{ agent_id: string; created: boolean; config: VysorAgentConfigPublic }>
    >(apiPath('/assistant/agent-config/provision-foundry-agent'));
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || response.data.message || 'Failed to provision Foundry agent');
    }
    return response.data.data;
  },
};

export { default as notificationsAPI } from './notificationsAPI';

export default api;
